import { createApi } from '@reduxjs/toolkit/dist/query/react'
import dayjs from 'dayjs'
import { getBaseQueryAuth } from 'helpers/rtkQueryHelpers'
import { v4 as uuid } from 'uuid'

import {
  TAppealsList,
  TAppeal,
  TTimeSlotList,
  TEditAppealRequest,
  TEditAppealResponse,
  TGetAppealRequestBody,
  TAppealManagerRequest,
  TAppealManagerResponse,
  AppealLoadingList,
  TAppoveOfferRequest,
  TGetConsultationResponse,
  TRecalculationResponse,
} from '../type'

import {
  setIsPullingStopped,
  setLoading,
  setAllLoadingFalse,
} from './AppealFormSlice'
import { FIAS_LEVEL } from 'constants/const'

export const appealApi = createApi({
  reducerPath: 'appeal',
  baseQuery: getBaseQueryAuth,
  refetchOnMountOrArgChange: true,
  tagTypes: ['appeal', 'appeals', ''],
  endpoints: builder => ({
    // получние заявки
    getAppeal: builder.query<TAppeal, TGetAppealRequestBody>({
      query: ({ uid, orderId }) => {
        const orderUrl = orderId ? `&orderId=${orderId}` : ''
        return {
          url: `/v1/order?uid=${uid}${orderUrl}`,
          method: 'GET',
        }
      },

      // после выполнения запроса скрыть все лоадеры
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(setAllLoadingFalse())
        } catch {
          // ошибки обрабатываются в getBaseQueryAuth и ErrorHandlerLayout
        }
      },

      transformResponse: (res: TAppeal) => {
        const modifiedResponse = {
          ...res,
          timeSlots: res.timeSlots
            ? [
                ...res.timeSlots.map(timeSlot => {
                  return {
                    ...timeSlot,
                    date: dayjs(timeSlot.dateFrom).format('YYYY-MM-DD'),
                    value: timeSlot.id,
                    key: uuid(),
                  }
                }),
              ]
            : null,
          location: {
            ...res.location,
            fiasId: '',
            fiasLevel: FIAS_LEVEL,
          },
        }

        return modifiedResponse
      },
      providesTags: ['appeal'],
    }),
    // получение списка заявок
    getAppeals: builder.query<TAppealsList, void>({
      query: () => ({
        url: `/v1/order/list`,
        method: 'GET',
      }),
      transformResponse: (res: { list: TAppealsList }) => {
        return res.list
      },
      providesTags: ['appeals'],
    }),
    // получение списка существующих заказов/заявок у пользователя
    checkIsAppealsExist: builder.query<TAppealsList, void>({
      query: () => ({
        url: `/v1/order/existing/list`,
        method: 'GET',
      }),
      transformResponse: (res: { list: TAppealsList }) => {
        return res.list
      },
    }),
    // получение актуальных таймслотов по fiasId
    getTimeSlots: builder.query<TTimeSlotList, string>({
      query: fiasId => ({
        url: `v1/region/time-slot/${fiasId}/list`,
        method: 'GET',
      }),
      providesTags: ['appeal'],
    }),
    // редактирвоание заявки
    editAppeal: builder.mutation<TEditAppealResponse, TEditAppealRequest>({
      query: appeal => {
        return {
          url: `/v1/order`,
          method: 'PUT',
          body: appeal,
        }
      },
      invalidatesTags: ['appeal'],

      async onQueryStarted(_, { dispatch }) {
        dispatch(
          setLoading({
            type: AppealLoadingList.appealInformation,
            value: true,
          }),
        )
      },
    }),
    // метод для переноса/отмены заявки, жалобы на диллера
    appealManager: builder.mutation<
      TAppealManagerResponse,
      TAppealManagerRequest
    >({
      query: body => ({
        url: `/v1/order/${body.url} `,
        method: 'POST',
        body: {
          uid: body.uid,
          comment: body.comment,
          orderId: body.orderId,
          datetime: body?.datetime,
        },
      }),
      invalidatesTags: ['appeal'],

      async onQueryStarted(_, { dispatch }) {
        dispatch(
          setLoading({
            type: AppealLoadingList.appealManager,
            value: true,
          }),
        )
      },
    }),
    // выбор КП
    approveOffer: builder.mutation<TTimeSlotList, TAppoveOfferRequest>({
      query: body => ({
        url: `v1/order/accept-commercial-offer`,
        method: 'POST',
        body: {
          uid: body.uid,
          orderId: body.orderId,
          offerId: body.offerId,
        },
      }),

      invalidatesTags: ['appeal'],

      async onQueryStarted(_, { dispatch }) {
        dispatch(
          setLoading({
            type: AppealLoadingList.appealOffers,
            value: true,
          }),
        )

        dispatch(setIsPullingStopped(true))
      },
    }),
    // получение консультации
    getConsultation: builder.query<TGetConsultationResponse | Error, string>({
      query: id => ({
        url: `v1/consultation/${id}`,
        method: 'GET',
      }),
      providesTags: ['appeal'],
    }),
    // пересчет КП
    offerRecalculation: builder.query<TRecalculationResponse | Error, number>({
      query: offerId => ({
        url: `v1/order/recalculation-offer/${offerId}`,
        method: 'GET',
      }),
    }),
  }),
})

export const {
  useGetAppealQuery,
  useGetTimeSlotsQuery,
  useGetAppealsQuery,
  useCheckIsAppealsExistQuery,
  useEditAppealMutation,
  useAppealManagerMutation,
  useApproveOfferMutation,
  useGetConsultationQuery,
  useLazyOfferRecalculationQuery,
} = appealApi
